import React from "react";
import NewsBanner from "./NewsBanner";
import NewsHeader from "./NewsHeader";
import NewsBody from "./NewsBody";
import CompanyTag from "./CompanyTag";
import NewsTags from "./NewsTags";
import { useNewsCardData } from "./useNewsCardData";
import { useNewsCardClick } from "./useNewsCardClick";
import { buttonResetStyles } from "./newsCardStyles";
import type { BaseNewsCardProps } from "./types";
import { trackComponent } from "@repo/analytics";
import {
  componentNameConstants,
  dlCtaTextConstants,
  dlEventConstants,
} from "@repo/analytics/data-layer.constants";
import { getCustomerIdForAnalytics } from "../../../utils/analytics-utils";
import {
  campaignName,
  journeyTag,
  productCategory,
  propertyType,
  referenceId,
  sprint,
  subSection,
} from "../../../pages/News/newsTrackingConstants";

const VerticalNewsCard: React.FC<BaseNewsCardProps> = ({
  newsItem,
  onClick,
  showContent = false,
  showTags = false,
  showCompanies = true,
  showTimestamp = true,
  className = "",
  aemImages,
  timestampLabels,
  newsType = "trending", // Default to trending as VerticalNewsCard is primarily used for trending
  componentPosition,
}) => {
  const data = useNewsCardData(newsItem, aemImages);
  const { handleClick } = useNewsCardClick({
    newsId: data.id,
    onClick,
    newsType,
    newsHeading: data.title,
    componentPosition: componentPosition ?? newsItem.id.toString(),
  });

  const handleReadMoreClick = () => {
    (async () => {
      const customerId = await getCustomerIdForAnalytics();

      await trackComponent({
        componentName: componentNameConstants.IPO_NEWS,
        sectionTitle: componentNameConstants.TRENDING_NEWS,
        eventType: dlEventConstants.IPO_NEWS_CARD_CLICK,
        ctaText: dlCtaTextConstants.READ_MORE,
        productCategory,
        subSection: data.title,
        cleverTapOverrides: {
          EP_JOURNEY: journeyTag,
          EP_PROPERTY_TYPE: propertyType,
          EP_PRODUCT_NAME: subSection,
          EP_CAMPAIGN: campaignName,
          EP_SPRINT: sprint,
          EP_REFERENCE_ID: referenceId,
          ...(customerId && { EP_CUSTOMER_ID: customerId }),
          EP_SR_NUMBER: componentPosition,
        },
      });
    })();
  };

  return (
    <button
      onClick={handleClick}
      aria-label={`View news: ${data.title}`}
      className={`flex relative min-h-[219px] max-w-[200px] shrink-0 cursor-pointer flex-col gap-3 rounded-[4px] border border-[#E5E5E5] bg-white p-3 transition-colors md:min-h-[268.56px] md:max-w-[250px] ${className}`}
      style={buttonResetStyles}
    >
      <NewsBanner
        bannerLink={data.bannerLink}
        title={data.title}
        className="h-[103px] w-[184px] rounded object-cover md:h-[127px] md:w-[226px]"
        aemImages={aemImages}
      />
      <div className="flex flex-1 flex-col gap-3">
        <NewsHeader
          sourceName={data.sourceName}
          sourceLogo={data.sourceLogo}
          timestamp={showTimestamp ? data.timestamp : undefined}
          aemImages={aemImages}
          timestampLabels={timestampLabels}
        />
        <NewsBody
          title={data.title}
          content={data.content}
          showContent={showContent}
        />
        <div className="mt-auto flex items-center gap-2 md:gap-[10px]">
          {showCompanies && data.companies?.[0]?.symbol && (
            <CompanyTag company={data.companies[0]} />
          )}
          <span
            className="font-rubik ml-auto text-[12px] font-medium leading-[18px] tracking-[0px] text-[#FF6700] md:text-[14px]"
            aria-hidden="true"
            onClick={handleReadMoreClick}
          >
            Read more
          </span>
        </div>
        {showTags && data.tags && data.tags.length > 0 && (
          <NewsTags tags={data.tags} />
        )}
      </div>
    </button>
  );
};

export default VerticalNewsCard;
